import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import Login from './pages/Login';
import Legal from './pages/Legal';
import Forecast from './pages/forecast/Forecast';
import { useAuth } from './hooks/useAuth';
import MarketAggregatesNew from './pages/market-aggregates-new/MarketAggregatesNew';
import LiveBenchmarking from './pages/live-benchmarking/LiveBenchmarking';
import { CookieNotice } from './components/CookieNotice';
import Power from './pages/Power';
import Stations from './pages/Stations';
import NotFound from './pages/NotFound';
import { AuthProvider } from './components/auth/AuthProvider';
import { AccessProvider } from './components/access';
import { PlatformProvider } from './contexts/PlatformContext';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import EmailLink from './pages/EmailLink';
import Account from './pages/Account';
import UserLanding from './pages/UserLanding';
import CompleteProfile from './pages/CompleteProfile';
import Waitlist from './pages/Waitlist';
import AccessError from './pages/AccessError';
import {
  PageErrorBoundary,
  AuthErrorBoundary,
  DashboardErrorBoundary,
} from './components/errors/RouteErrorBoundaries';
import { publicRoutes, profileCompletionExemptRoutes } from './utils/routes';
import { AnalyticsIdentifier } from './components/analytics/AnalyticsIdentifier';
import { TimezoneProvider } from './contexts/TimezoneContext';
import { MapSettingsProvider } from './contexts/MapSettingsContext';
import MapViewPage from './pages/MapViewPage';
import Benchmarks from './pages/benchmarks/Benchmarks';
import { DemoProvider } from './components/demo/DemoProvider';
import SplashScreen from './components/auth/SplashScreen';
import DisseminationStatus from './pages/dissemination-status/DisseminationStatus';

export function cookieConsentGiven(): string {
  return localStorage.getItem('cookie-consent') ?? 'undecided';
}

function App() {
  const { isAuthenticated, isLoading, needsProfileCompletion } = useAuth();
  const location = useLocation();

  const [consentGiven, setConsentGiven] = useState('');
  const [showSplash, setShowSplash] = useState(false);

  const onCookieAccept = () => {
    setConsentGiven('true');
  };

  useEffect(() => {
    setConsentGiven(cookieConsentGiven());
  }, []);

  useEffect(() => {
    if (consentGiven !== '') {
      posthog.set_config({
        persistence: consentGiven === 'true' ? 'localStorage+cookie' : 'memory',
      });
    }
  }, [consentGiven]);

  // Show splash once per session right after authentication
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const alreadyShown = sessionStorage.getItem('splash-shown');
      if (!alreadyShown) {
        setShowSplash(true);
        const timer = setTimeout(() => {
          setShowSplash(false);
          sessionStorage.setItem('splash-shown', 'true');
        }, 1400);
        return () => clearTimeout(timer);
      }
    }
  }, [isLoading, isAuthenticated]);

  // Show loading spinner while checking auth state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E34290]"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated and not on a public route
  if (!isAuthenticated && !publicRoutes.includes(location.pathname)) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // If splash is showing, render it before any onboarding/profile redirects
  if (showSplash) {
    return <SplashScreen />;
  }

  // If authenticated but needs profile completion, redirect to complete-profile
  // Exclude password reset and invitation flows from profile completion check
  if (
    isAuthenticated &&
    needsProfileCompletion() &&
    location.pathname !== '/complete-profile' &&
    !profileCompletionExemptRoutes.includes(location.pathname)
  ) {
    return <Navigate to="/complete-profile" replace state={{ from: location }} />;
  }

  return (
    <TimezoneProvider>
      <AuthProvider>
        <AccessProvider>
          <DemoProvider>
            <PlatformProvider>
              <AnalyticsIdentifier />
              <Routes>
                {/* Public routes with auth error boundary */}
                <Route
                  path="/waitlist"
                  element={
                    <AuthErrorBoundary>
                      <Waitlist />
                    </AuthErrorBoundary>
                  }
                />
                <Route
                  path="/login"
                  element={
                    <AuthErrorBoundary>
                      <Login />
                    </AuthErrorBoundary>
                  }
                />
                <Route
                  path="/auth"
                  element={
                    <AuthErrorBoundary>
                      <Login />
                    </AuthErrorBoundary>
                  }
                />
                <Route
                  path="/login/confirm"
                  element={
                    <AuthErrorBoundary>
                      <EmailLink />
                    </AuthErrorBoundary>
                  }
                />
                <Route
                  path="/login/recover"
                  element={
                    <AuthErrorBoundary>
                      <EmailLink />
                    </AuthErrorBoundary>
                  }
                />
                <Route
                  path="/login/accept-invitation"
                  element={
                    <AuthErrorBoundary>
                      <EmailLink />
                    </AuthErrorBoundary>
                  }
                />
                <Route
                  path="/login/magic-link"
                  element={
                    <AuthErrorBoundary>
                      <EmailLink />
                    </AuthErrorBoundary>
                  }
                />

                {/* Profile completion route - only accessible when authenticated */}
                <Route
                  path="/complete-profile"
                  element={
                    <AuthErrorBoundary>
                      <CompleteProfile />
                    </AuthErrorBoundary>
                  }
                />

                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <DashboardErrorBoundary>
                        <Layout />
                      </DashboardErrorBoundary>
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/overview" replace />} />
                  <Route path="overview" element={<UserLanding />} />
                  <Route path="forecast" element={<Forecast />} />
                  <Route
                    path="map"
                    element={
                      <MapSettingsProvider>
                        <MapViewPage />
                      </MapSettingsProvider>
                    }
                  />
                  <Route path="power" element={<Power />} />
                  <Route path="power/:tab" element={<Power />} />
                  <Route path="market-aggregates" element={<MarketAggregatesNew />} />
                  <Route path="live-benchmarking" element={<LiveBenchmarking />} />
                  <Route path="benchmarks" element={<Benchmarks />} />
                  <Route path="stations" element={<Stations />} />
                  <Route path="account" element={<Account />} />
                  <Route path="dissemination-status" element={<DisseminationStatus />} />
                  <Route path="legal" element={<Legal />} />
                  <Route path="error" element={<AccessError />} />
                </Route>

                {/* Catch-all route for 404 with page error boundary */}
                <Route
                  path="*"
                  element={
                    <PageErrorBoundary>
                      <NotFound />
                    </PageErrorBoundary>
                  }
                />
              </Routes>
              <CookieNotice onAccept={onCookieAccept} />
            </PlatformProvider>
          </DemoProvider>
        </AccessProvider>
      </AuthProvider>
    </TimezoneProvider>
  );
}

export default App;
